import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AppStatus,
  AgentRole,
  type Chapter,
  type Ledger,
  type LogEntry,
  type ProjectMetadata,
  type StoryBible,
  type StorySeed,
} from '../types';
import { useFirebase } from '../../components/FirebaseProvider';
import ProjectDashboard from '../../components/ProjectDashboard';
import StorySetup from '../../components/StorySetup';
import BlueprintReview from '../../components/BlueprintReview';
import { PublicationExport } from '../../components/PublicationExport';
import { PaywallModal } from '../../components/PaywallModal';
import LogPanel from '../../components/LogPanel';
import { LegalGate } from '../../components/LegalGate';
import { ChronovelaSignInScreen } from '../../components/ChronovelaSignInScreen';
import { doc, setDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { OriginalEngineCompatRunner } from './OriginalEngineCompatRunner';
import { OriginalEngineCompatSequelRunner } from './OriginalEngineCompatSequelRunner';
import { MockOriginalEngineAITransport } from './adapters/MockOriginalEngineAITransport';
import { GeminiTransportAdapter } from './adapters/GeminiTransportAdapter';
import { createCompatTransportStack } from './adapters/boundedCompatTransport';
import { ManuscriptEntitlementAdapter } from './adapters/ManuscriptEntitlementAdapter';
import {
  InMemoryOriginalEngineProjectRepository,
  InMemoryOriginalEngineSequelRepository,
} from './adapters/InMemoryCompatRepository';
import {
  createFirestoreOriginalEngineRepository,
  createFirestoreOriginalSequelRepository,
  loadOriginalEngineProjectList,
  mapOriginalAgentToProgressLabel,
} from './adapters/firestoreOriginalEngineRepository';
import { DEMO_FIXTURE_MOCK_RESPONSES, buildDemoFixtureProjectState } from './fixtures/demoChapter1Mocks';
import { initializeStoryState, planChapters } from './golden/gemini.agents';
import { createGoldenGenerateBinding } from './adapters/goldenGenerateBinding';
import { filterUserProjects } from '../utils/projectLoader';
import { resolveWorkspaceStatus } from './compat/compatPersistence.service';
import type { OriginalCompatCheckpointMetadata } from './compat/compatTypes';
import type { OriginalEngineCompatRepository } from './types';
import type { PaywallModalState } from '../utils/manuscriptCreditRouting.service';

const USE_MOCK_TRANSPORT = import.meta.env.VITE_ORIGINAL_ENGINE_MOCK === 'true';
const IS_STAGING = import.meta.env.VITE_STAGING_MODE === 'true';
const CURRENT_LEGAL_VERSION = '2026-06';

function createProjectId(): string {
  return Math.random().toString(36).substring(2, 15);
}

function seedToStoryBible(seed: StorySeed): StoryBible {
  return {
    title: seed.title,
    genre: seed.genre,
    premise: seed.premise,
    style_guide: seed.style_guide,
    mode: seed.mode,
    targetChapterCount: seed.targetChapterCount,
    chapterLength: seed.chapterLength,
    isClosingMode: false,
    volume: 1,
    chapterPlans: [],
  };
}

function clearProjectRuntimeState(setters: {
  setStoryBible: (v: StoryBible | null) => void;
  setLedger: (v: Ledger | null) => void;
  setChapters: (v: Chapter[]) => void;
  setLogs: (v: LogEntry[]) => void;
  setCurrentChapterNum: (v: number) => void;
  setRecoveryCheckpoint: (v: OriginalCompatCheckpointMetadata | null) => void;
  setIntegrityBlocked: (v: boolean) => void;
}) {
  setters.setStoryBible(null);
  setters.setLedger(null);
  setters.setChapters([]);
  setters.setLogs([]);
  setters.setCurrentChapterNum(1);
  setters.setRecoveryCheckpoint(null);
  setters.setIntegrityBlocked(false);
}

export function OriginalEngineWorkspace() {
  const {
    user,
    userData,
    reserveTokens,
    consumeTokenReservation,
    releaseTokenReservation,
    applyManuscriptCreditToProject,
    updateUserData,
    refreshUserData,
  } = useFirebase();

  const [status, setStatus] = useState<AppStatus>(AppStatus.DASHBOARD);
  const [projects, setProjects] = useState<ProjectMetadata[]>([]);
  const [projectsLoadState, setProjectsLoadState] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [currentProjectId, setCurrentProjectId] = useState<string | null>(null);
  const [storyBible, setStoryBible] = useState<StoryBible | null>(null);
  const [ledger, setLedger] = useState<Ledger | null>(null);
  const [chapters, setChapters] = useState<Chapter[]>([]);
  const [currentChapterNum, setCurrentChapterNum] = useState(1);
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [isInitializing, setIsInitializing] = useState(false);
  const [isGeneratingRoadmap, setIsGeneratingRoadmap] = useState(false);
  const [isGeneratingChapter, setIsGeneratingChapter] = useState(false);
  const [isCreatingSequel, setIsCreatingSequel] = useState(false);
  const [paywallState, setPaywallState] = useState<PaywallModalState | null>(null);
  const [recoveryCheckpoint, setRecoveryCheckpoint] = useState<OriginalCompatCheckpointMetadata | null>(null);
  const [integrityBlocked, setIntegrityBlocked] = useState(false);
  const [activeRunId, setActiveRunId] = useState<string | null>(null);
  const generateAbortRef = useRef(false);

  const addLog = useCallback((agent: AgentRole | 'System', message: string, type: LogEntry['type'] = 'info') => {
    setLogs((prev) => [
      ...prev,
      {
        id: `${Date.now()}-${prev.length}`,
        timestamp: new Date().toLocaleTimeString(),
        agent,
        message,
        type,
        audience: 'user',
      },
    ]);
  }, []);

  const refreshProjects = useCallback(async () => {
    if (!user || USE_MOCK_TRANSPORT) {
      setProjectsLoadState('success');
      return;
    }
    setProjectsLoadState('loading');
    try {
      const loaded = await loadOriginalEngineProjectList(user.uid);
      setProjects(filterUserProjects(loaded, user.uid));
      setProjectsLoadState('success');
    } catch {
      setProjectsLoadState('error');
    }
  }, [user]);

  useEffect(() => {
    void refreshProjects();
  }, [refreshProjects]);

  const transport = useMemo(() => {
    if (USE_MOCK_TRANSPORT) {
      return new MockOriginalEngineAITransport(DEMO_FIXTURE_MOCK_RESPONSES);
    }
    const { transport: bounded } = createCompatTransportStack(
      new GeminiTransportAdapter({ maxClientTransportRetries: 1 }),
    );
    return bounded;
  }, []);

  const entitlement = useMemo(() => {
    if (!reserveTokens) {
      return {
        async reserve(input: { runId: string }) {
          return { reservationId: input.runId, runId: input.runId };
        },
        async consume() {},
        async release() {},
      };
    }
    return new ManuscriptEntitlementAdapter({
      reserveTokens,
      consumeTokenReservation: consumeTokenReservation!,
      releaseTokenReservation: releaseTokenReservation!,
    });
  }, [reserveTokens, consumeTokenReservation, releaseTokenReservation]);

  const repository = useMemo((): OriginalEngineCompatRepository | null => {
    if (USE_MOCK_TRANSPORT) {
      const repo = new InMemoryOriginalEngineProjectRepository();
      if (currentProjectId) {
        const seed = buildDemoFixtureProjectState(currentProjectId);
        repo.seedProject(seed);
      }
      return repo as OriginalEngineCompatRepository;
    }
    if (!user) return null;
    return createFirestoreOriginalEngineRepository(user.uid);
  }, [user, currentProjectId]);

  const sequelRepository = useMemo(() => {
    if (USE_MOCK_TRANSPORT) {
      const repo = new InMemoryOriginalEngineSequelRepository();
      if (currentProjectId && storyBible && ledger) {
        repo.seedProject({
          projectId: currentProjectId,
          storyBible,
          ledger,
          chapters,
          currentChapterNumber: currentChapterNum,
        });
      }
      return repo;
    }
    if (!user) return null;
    return createFirestoreOriginalSequelRepository(user.uid);
  }, [user, currentProjectId, storyBible, ledger, chapters, currentChapterNum]);

  const openProject = useCallback(async (projectId: string) => {
    generateAbortRef.current = true;
    clearProjectRuntimeState({
      setStoryBible,
      setLedger,
      setChapters,
      setLogs,
      setCurrentChapterNum,
      setRecoveryCheckpoint,
      setIntegrityBlocked,
    });
    setCurrentProjectId(projectId);
    generateAbortRef.current = false;

    if (USE_MOCK_TRANSPORT) {
      const seed = buildDemoFixtureProjectState(projectId);
      setStoryBible(seed.storyBible);
      setLedger(seed.ledger);
      setChapters(seed.chapters);
      setCurrentChapterNum(seed.currentChapterNumber);
      setStatus(resolveWorkspaceStatus(seed.storyBible) === 'IDLE' ? AppStatus.IDLE : AppStatus.BLUEPRINT_REVIEW);
      return;
    }

    if (!repository) return;
    try {
      const loaded = await repository.loadFullProject(projectId);
      setStoryBible(loaded.storyBible);
      setLedger(loaded.ledger);
      setChapters(loaded.chapters);
      setCurrentChapterNum(loaded.currentChapterNumber);
      setRecoveryCheckpoint(loaded.recoverableCheckpoint);
      setStatus(resolveWorkspaceStatus(loaded.storyBible) === 'IDLE' ? AppStatus.IDLE : AppStatus.BLUEPRINT_REVIEW);
    } catch (error: any) {
      addLog('System', error?.message || 'Failed to open project', 'error');
    }
  }, [repository, addLog]);

  const persistState = useCallback(async () => {
    if (!user || !currentProjectId || !storyBible || !ledger || USE_MOCK_TRANSPORT) return;
    await patchDocSafe(user.uid, currentProjectId, storyBible, ledger, chapters, currentChapterNum);
  }, [user, currentProjectId, storyBible, ledger, chapters, currentChapterNum]);

  const handleInitialize = useCallback(async (seed: StorySeed) => {
    setIsInitializing(true);
    const projectId = createProjectId();
    try {
      createGoldenGenerateBinding(transport, { projectId, chapterNumber: 1 });
      const bible = seedToStoryBible(seed);
      const initializedLedger = await initializeStoryState(seed);
      setCurrentProjectId(projectId);
      setStoryBible(bible);
      setLedger(initializedLedger);
      setChapters([]);
      setCurrentChapterNum(1);
      setLogs([]);
      addLog('System', `Project "${seed.title}" created.`, 'success');

      if (!USE_MOCK_TRANSPORT && user) {
        const now = new Date().toISOString();
        await setDoc(doc(db, 'users', user.uid, 'projects', projectId), {
          id: projectId,
          title: seed.title,
          genre: seed.genre.map((g) => g.name).join(', '),
          lastUpdated: now,
          chapterCount: 0,
          mode: seed.mode,
          volume: 1,
          status: AppStatus.BLUEPRINT_REVIEW,
          userId: user.uid,
        });
        await setDoc(doc(db, 'users', user.uid, 'projects', projectId, 'state', 'current'), {
          storyBible: bible,
          ledger: initializedLedger,
          chapters: [],
          currentChapterNum: 1,
          logs: [],
          lastUpdated: now,
        });
        await refreshProjects();
      }

      setStatus(AppStatus.BLUEPRINT_REVIEW);
    } catch (error: any) {
      addLog('System', error?.message || 'Initialization failed', 'error');
    } finally {
      setIsInitializing(false);
    }
  }, [transport, user, addLog, refreshProjects]);

  const handleApproveBlueprint = useCallback(async () => {
    if (!storyBible || !ledger || !repository || !currentProjectId) return;
    await repository.persistBlueprintApproval(currentProjectId, storyBible, ledger);
    const reloaded = await repository.loadProject(currentProjectId);
    setStoryBible(reloaded.storyBible);
    setLedger(reloaded.ledger);
    addLog('System', 'Blueprint approved and saved.', 'success');
  }, [storyBible, ledger, repository, currentProjectId, addLog]);

  const handleGenerateRoadmap = useCallback(async () => {
    if (!storyBible || !ledger || !currentProjectId) return;
    setIsGeneratingRoadmap(true);
    try {
      createGoldenGenerateBinding(transport, { projectId: currentProjectId, chapterNumber: 1 });
      const plans = await planChapters(storyBible, ledger);
      const updated = { ...storyBible, chapterPlans: plans };
      setStoryBible(updated);
      if (user && !USE_MOCK_TRANSPORT && currentProjectId) {
        await patchDocSafe(user.uid, currentProjectId, updated, ledger, chapters, currentChapterNum);
      }
      addLog(AgentRole.ARCHITECT, 'Roadmap generated.', 'success');
    } catch (error: any) {
      addLog('System', error?.message || 'Roadmap generation failed', 'error');
    } finally {
      setIsGeneratingRoadmap(false);
    }
  }, [storyBible, ledger, currentProjectId, transport, repository, addLog]);

  const handleApproveRoadmap = useCallback(async () => {
    if (!storyBible || !ledger || !repository || !currentProjectId) return;
    await repository.persistRoadmapApproval(currentProjectId, storyBible, ledger);
    const reloaded = await repository.loadProject(currentProjectId);
    setStoryBible(reloaded.storyBible);
    setLedger(reloaded.ledger);
    addLog('System', 'Roadmap approved and saved.', 'success');
    setStatus(AppStatus.IDLE);
  }, [storyBible, ledger, repository, currentProjectId, addLog]);

  const handleGenerateChapter = useCallback(async (resume = false) => {
    if (!repository || !currentProjectId || generateAbortRef.current) return;
    setIsGeneratingChapter(true);
    addLog('System', mapOriginalAgentToProgressLabel('SCRIBE'), 'action');
    const runId = activeRunId || `compat-${Date.now()}`;
    setActiveRunId(runId);

    const runner = new OriginalEngineCompatRunner({
      transport,
      repository,
      entitlement,
      engineMode: 'ORIGINAL_ENGINE_COMPAT',
      uid: user?.uid,
    });

    const result = await runner.run({
      projectId: currentProjectId,
      runId,
      chapterNumber: currentChapterNum,
      resumeFromCheckpoint: resume || Boolean(recoveryCheckpoint),
      forceStagingFailureAfter:
        IS_STAGING && import.meta.env.VITE_ORIGINAL_ENGINE_STAGING_FAILURE_INJECT === 'EDITOR_COMPLETE'
          ? 'EDITOR_COMPLETE'
          : undefined,
    });

    if (result.ok) {
      addLog(AgentRole.ARCHIVIST, mapOriginalAgentToProgressLabel('ARCHIVIST'), 'success');
      addLog('System', `Chapter complete: ${result.chapterTitle}`, 'success');
      setRecoveryCheckpoint(null);
      setIntegrityBlocked(false);
      setActiveRunId(null);
      const reloaded = await repository.loadFullProject(currentProjectId);
      setStoryBible(reloaded.storyBible);
      setLedger(reloaded.ledger);
      setChapters(reloaded.chapters);
      setCurrentChapterNum(reloaded.currentChapterNumber);
    } else {
      if (result.openPaywall) {
        setPaywallState({
          isOpen: true,
          reason: 'generation',
          headline: 'A manuscript credit is required.',
          subheadline: 'Apply an existing credit or purchase a package.',
        });
      } else if (result.integrityBlocked) {
        setIntegrityBlocked(true);
      } else if (result.checkpointRetained) {
        const reloaded = await repository.loadFullProject(currentProjectId);
        setRecoveryCheckpoint(reloaded.recoverableCheckpoint);
      }
      addLog('System', result.message, 'error');
    }
    setIsGeneratingChapter(false);
  }, [
    repository,
    currentProjectId,
    transport,
    entitlement,
    currentChapterNum,
    addLog,
    user,
    recoveryCheckpoint,
    activeRunId,
  ]);

  const handleDiscardCheckpoint = useCallback(async () => {
    if (!repository || !currentProjectId) return;
    await repository.discardCheckpoint(currentProjectId, currentChapterNum);
    setRecoveryCheckpoint(null);
    setIntegrityBlocked(false);
    setActiveRunId(null);
    addLog('System', 'Recovery checkpoint discarded.', 'info');
  }, [repository, currentProjectId, currentChapterNum, addLog]);

  const handleCreateSequel = useCallback(async () => {
    if (!sequelRepository || !currentProjectId) return;
    setIsCreatingSequel(true);
    addLog('System', 'Creating next volume workspace…', 'action');
    const runner = new OriginalEngineCompatSequelRunner({ transport, repository: sequelRepository });
    const result = await runner.createSequel({ sourceProjectId: currentProjectId });
    if (result.ok) {
      await openProject(result.newProjectId);
      addLog('System', `Volume ${result.volume} workspace ready.`, 'success');
      setStatus(AppStatus.BLUEPRINT_REVIEW);
      await refreshProjects();
    } else {
      addLog('System', result.message, 'error');
    }
    setIsCreatingSequel(false);
  }, [sequelRepository, currentProjectId, transport, addLog, openProject, refreshProjects]);

  const handleDeleteProject = useCallback(async (projectId: string) => {
    if (!user || USE_MOCK_TRANSPORT) return;
    await deleteDoc(doc(db, 'users', user.uid, 'projects', projectId));
    if (currentProjectId === projectId) {
      clearProjectRuntimeState({
        setStoryBible,
        setLedger,
        setChapters,
        setLogs,
        setCurrentChapterNum,
        setRecoveryCheckpoint,
        setIntegrityBlocked,
      });
      setCurrentProjectId(null);
      setStatus(AppStatus.DASHBOARD);
    }
    await refreshProjects();
  }, [user, currentProjectId, refreshProjects]);

  if (!user && !USE_MOCK_TRANSPORT) {
    return (
      <ChronovelaSignInScreen
        subtitle="Sign in to use Chronovela Original Engine."
        showStagingBanner={IS_STAGING}
      />
    );
  }

  if (user && userData && !userData.legalAccepted) {
    return <LegalGate onAccept={updateUserData} currentLegalVersion={CURRENT_LEGAL_VERSION} />;
  }

  return (
    <>
      {IS_STAGING && (
        <div className="bg-amber-600 text-black text-xs text-center py-1 font-semibold tracking-wide">
          STAGING — Original Engine Compatibility
        </div>
      )}

      {status === AppStatus.DASHBOARD && (
        <ProjectDashboard
          projects={projects}
          projectsLoadState={projectsLoadState}
          onRetryProjectsLoad={() => void refreshProjects()}
          onSelect={(id) => void openProject(id)}
          onDelete={(id) => void handleDeleteProject(id)}
          onNew={() => setStatus(AppStatus.SETUP)}
          onUpgradePlan={() => setPaywallState({ isOpen: true, reason: 'generation' })}
        />
      )}

      {status === AppStatus.SETUP && (
        <StorySetup
          onInitialize={(seed) => void handleInitialize(seed)}
          isLoading={isInitializing}
          hasSavedGame={false}
          onResume={() => setStatus(AppStatus.DASHBOARD)}
        />
      )}

      {status === AppStatus.BLUEPRINT_REVIEW && storyBible && ledger && (
        <BlueprintReview
          storyBible={storyBible}
          ledger={ledger}
          onUpdate={(b, l) => {
            setStoryBible(b);
            setLedger(l);
          }}
          onApproveBlueprint={() => void handleApproveBlueprint()}
          onApproveRoadmap={() => void handleApproveRoadmap()}
          onEnterNarrativeHub={async () => setStatus(AppStatus.IDLE)}
          onGenerateRoadmap={() => handleGenerateRoadmap()}
          onBackToSetup={() => setStatus(AppStatus.SETUP)}
          isGeneratingRoadmap={isGeneratingRoadmap}
        />
      )}

      {status === AppStatus.IDLE && storyBible && ledger && currentProjectId && (user || USE_MOCK_TRANSPORT) && (
        <div className="min-h-screen bg-cv-page text-cv-primary p-6">
          <header className="mb-6 flex flex-wrap items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold">{storyBible.title}</h1>
              <p className="text-cv-secondary text-sm">
                Volume {storyBible.volume} · Chapter {currentChapterNum} · Original Engine
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <button type="button" className="px-3 py-2 bg-cv-panel border border-cv-border rounded text-sm" onClick={() => setStatus(AppStatus.DASHBOARD)}>
                Projects
              </button>
              <button type="button" className="px-3 py-2 bg-cv-panel border border-cv-border rounded text-sm" onClick={() => setStatus(AppStatus.BLUEPRINT_REVIEW)}>
                Blueprint & Roadmap
              </button>
              <button
                type="button"
                className="px-4 py-2 bg-cv-interactive text-white rounded font-semibold disabled:opacity-40"
                disabled={isGeneratingChapter}
                onClick={() => void handleGenerateChapter(Boolean(recoveryCheckpoint))}
              >
                {isGeneratingChapter ? 'Generating…' : recoveryCheckpoint ? 'Resume Chapter' : 'Generate Chapter'}
              </button>
              <button
                type="button"
                className="px-3 py-2 bg-cv-panel border border-cv-border rounded text-sm disabled:opacity-40"
                disabled={isCreatingSequel || chapters.length === 0}
                onClick={() => void handleCreateSequel()}
              >
                {isCreatingSequel ? 'Creating sequel…' : 'Create Next Volume'}
              </button>
            </div>
          </header>

          {(recoveryCheckpoint || integrityBlocked) && (
            <div className="mb-4 p-3 bg-cv-panel border border-cv-border rounded text-sm">
              {integrityBlocked ? (
                <>
                  <p className="font-semibold">Recovery blocked</p>
                  <p className="text-cv-secondary">Project canon or roadmap changed since this checkpoint was saved.</p>
                </>
              ) : (
                <>
                  <p className="font-semibold">Paused at {recoveryCheckpoint?.resumeStage?.replace(/_/g, ' ').toLowerCase()}</p>
                  <p className="text-cv-muted text-xs mt-1">Your draft is preserved. Resume when ready — prose was not deleted.</p>
                </>
              )}
              <div className="flex gap-2 mt-2">
                {!integrityBlocked && (
                  <button type="button" className="px-3 py-1 bg-cv-interactive text-white rounded text-sm" onClick={() => void handleGenerateChapter(true)}>
                    Resume Chapter
                  </button>
                )}
                <button type="button" className="px-3 py-1 bg-cv-panel border border-cv-border rounded text-sm" onClick={() => void handleDiscardCheckpoint()}>
                  Discard Checkpoint
                </button>
                <button type="button" className="px-3 py-1 bg-cv-panel border border-cv-border rounded text-sm" onClick={() => setStatus(AppStatus.DASHBOARD)}>
                  Return to Projects
                </button>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2">
              <PublicationExport
                uid={user?.uid || 'mock-user'}
                projectId={currentProjectId}
                storyBible={storyBible}
                chapters={chapters.filter((c) => c.status === 'complete' || (c.content && c.content.length > 0))}
                onClose={() => {}}
              />
            </div>
            <LogPanel logs={logs} />
          </div>

          {USE_MOCK_TRANSPORT && (
            <p className="text-xs text-cv-muted mt-4">Mock transport active (VITE_ORIGINAL_ENGINE_MOCK=true)</p>
          )}
        </div>
      )}

      {paywallState?.isOpen && user && (
        <PaywallModal
          isOpen={paywallState.isOpen}
          reason={paywallState.reason}
          highlightedTier={paywallState.highlightedTier}
          headline={paywallState.headline}
          subheadline={paywallState.subheadline}
          onClose={() => {
            setPaywallState(null);
            void refreshUserData?.();
          }}
          user={user}
          userData={userData}
          updateUserData={updateUserData}
          refreshUserData={refreshUserData}
          applyManuscriptCreditToProject={applyManuscriptCreditToProject}
          storyBible={storyBible}
          setStoryBible={setStoryBible}
          currentProjectId={currentProjectId}
          addLog={addLog}
          saveCurrentProject={() => void persistState()}
          setStatus={setStatus}
          setIsAutoRunning={() => {}}
        />
      )}
    </>
  );
}

async function patchDocSafe(
  uid: string,
  projectId: string,
  storyBible: StoryBible,
  ledger: Ledger,
  chapters: Chapter[],
  currentChapterNum: number,
) {
  const { patchDoc } = await import('../utils/firestoreWrite');
  await patchDoc(doc(db, 'users', uid, 'projects', projectId, 'state', 'current'), {
    storyBible,
    ledger,
    chapters,
    currentChapterNum,
    lastUpdated: new Date().toISOString(),
  });
}

export default OriginalEngineWorkspace;
